// Billetsalg — the guest-facing event list and checkout (TICKETING-SPEC.md §4).
//
// Its own page at /events/:restaurantId rather than a section inside
// RestaurantMenu, for the same reason the backend gave tickets their own
// checkout: a ticket order shares none of the food flow's machinery — no
// opening hours, no table, no delivery, no bag fee — and threading it through
// the menu page would put every food order at risk to add a feature none of
// them use.
//
// The guest user is minted through the SAME POST /orders/guest/createuser every
// other page here uses, and the same 'guest-uui' localStorage key, so a guest
// who has already ordered food is not made into a second person.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import configData from '../../config.json';
import { PRIVACY_URL } from './Terms';
import './styles.css';

const API = (configData && configData.SERVER_URL) || 'http://localhost:5001/api';

// Restaurant.Currency is a currencies.json entry ({ code, symbol_native, … })
// on most venues and a plain string on a few; rendered raw, the object printed
// as "150,00 [object Object]".
const currencyLabel = (currency) =>
  (currency && typeof currency === 'object' ? currency.symbol_native || currency.code : currency) ||
  'kr';

const money = (n, currency) =>
  `${(Number(n) || 0).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyLabel(currency)}`;

const formatWhen = (event) => {
  if (!event.date) return '';
  const d = new Date(`${event.date}T00:00:00`);
  if (isNaN(d.getTime())) return [event.date, event.start].filter(Boolean).join(' ');
  return `${d.toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short' })}${event.start ? ` kl. ${event.start}` : ''}`;
};

// Why a sale is not open, in the guest's words. The server sends codes; these
// are the only place they become sentences.
const SALE_TEXT = {
  sales_not_started: 'Salget er ikke åbnet endnu',
  sales_ended: 'Salget er lukket',
  event_cancelled: 'Aflyst',
  event_not_published: 'Ikke til salg',
};

export default function Events() {
  const { restaurantId } = useParams();

  const [events, setEvents] = useState([]);
  const [currency, setCurrency] = useState('kr');
  const [ticketFee, setTicketFee] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [selected, setSelected] = useState(null); // the event being bought
  const [qty, setQty] = useState({}); // { ticketTypeId: n }
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [answer, setAnswer] = useState('');
  // The venue's marketing questions ({ email, sms } texts), or null when it does
  // not ask. E-mail and SMS are separate permissions, so two boxes, and both
  // always start unticked — consent is something the guest does, not a default.
  const [consentTexts, setConsentTexts] = useState(null);
  const [venueName, setVenueName] = useState('');
  // The venue's own terms link, or null for the standard terms page.
  const [termsUrl, setTermsUrl] = useState(null);
  const [consentEmail, setConsentEmail] = useState(false);
  const [consentSms, setConsentSms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  /*
   * Returns the freshly loaded events, so a caller that has just lost a race
   * for the last seat can re-point `selected` at the updated copy rather than
   * leaving a sold-out event looking available.
   */
  const loadEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets/public/${restaurantId}`);
      setEnabled(res.data.ticketingEnabled !== false);
      setEvents(res.data.events || []);
      setCurrency(res.data.currency || 'kr');
      setTicketFee(Number(res.data.ticketFee) || 0);
      setConsentTexts(res.data.marketingConsent || null);
      setVenueName(res.data.venueName || '');
      setTermsUrl(res.data.termsUrl || null);
      return res.data.events || [];
    } catch (err) {
      setLoadError('Arrangementerne kunne ikke hentes. Prøv igen om lidt.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Back from the payment window via the browser's Back button: Safari restores
  // this page from its cache with the buy button still on "Et øjeblik…".
  useEffect(() => {
    const onShow = (e) => {
      if (e.persisted) setBusy(false);
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  const open = (event) => {
    setSelected(event);
    setQty({});
    setError('');
    setAnswer('');
  };

  /*
   * The stepper stops at the event's own cap rather than a hardcoded 10, so a
   * venue that says "two each" gets a stepper that says two. The server checks
   * it again — this only spares the guest building a basket it will refuse.
   */
  const setCount = (typeId, value) => {
    const cap = Number(selected && selected.maxPerOrder) || 10;
    const others = Object.entries(qty).reduce(
      (sum, [id, n]) => (id === typeId ? sum : sum + (Number(n) || 0)),
      0,
    );
    const n = Math.max(0, Math.min(Math.max(0, cap - others), Number(value) || 0));
    setQty((prev) => ({ ...prev, [typeId]: n }));
  };

  /*
   * The totals shown here are a PREVIEW. The server prices the basket from the
   * event when the order is placed and that number is the one charged — the
   * client never sends a price. If the two ever disagree, the server is right.
   */
  const preview = useMemo(() => {
    if (!selected) return { count: 0, tickets: 0, fees: 0, total: 0, anyPaid: false };
    let count = 0;
    let tickets = 0;
    let fees = 0;
    for (const type of selected.ticketTypes || []) {
      const n = qty[type._id] || 0;
      const price = Number(type.price) || 0;
      count += n;
      tickets += n * price;
      // Free tickets carry no fee — the same rule the server prices by.
      if (price > 0) fees += n * ticketFee;
    }
    const round2 = (x) => Math.round(x * 100) / 100;
    return {
      count,
      tickets: round2(tickets),
      fees: round2(fees),
      total: round2(tickets + fees),
      anyPaid: (selected.ticketTypes || []).some((t) => (Number(t.price) || 0) > 0),
    };
  }, [selected, qty, ticketFee]);

  const guestId = async (fresh = false) => {
    const stored = fresh ? null : localStorage.getItem('guest-uui');
    if (stored && /^[a-f\d]{24}$/i.test(stored)) return stored;
    const resp = await axios.post(`${API}/orders/guest/createuser`, { fromWebStandAlone: true });
    const id = String(resp.data._id);
    localStorage.setItem('guest-uui', id);
    return id;
  };

  const buy = async () => {
    if (busy) return;
    setError('');

    if (preview.count === 0) {
      setError('Vælg mindst én billet.');
      return;
    }
    if (!name.trim() || !phone.trim()) {
      setError('Udfyld navn og telefonnummer.');
      return;
    }
    // A yes to e-mail with no address is a yes to nothing; the server refuses
    // it too, but saying so here keeps the guest out of a round trip.
    if (consentEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Skriv din e-mail, hvis du vil have nyheder på e-mail.');
      return;
    }
    if (selected.extraQuestion && selected.extraQuestion.Required && !answer.trim()) {
      setError(`Besvar venligst: ${selected.extraQuestion.Label}`);
      return;
    }

    setBusy(true);
    try {
      const here = window.location.origin;

      const send = async (id) =>
        axios.post(
          `${API}/tickets/checkout`,
          {
            restaurantid: restaurantId,
            eventId: selected._id,
            tickets: (selected.ticketTypes || [])
              .filter((t) => (qty[t._id] || 0) > 0)
              .map((t) => ({ ticketTypeId: t._id, qty: qty[t._id] })),
            guestId: id,
            customerName: name.trim(),
            mobilenumber: phone.trim(),
            email: email.trim() || undefined,
            extraAnswer: answer.trim() || undefined,
            // Only ever real trues. The server stores its own copy of the words.
            marketingConsent: consentTexts
              ? { email: consentEmail === true, sms: consentSms === true }
              : undefined,
            successuri: `${here}/events/${restaurantId}/kvittering`,
            failuri: `${here}/events/${restaurantId}/kvittering`,
          },
          { validateStatus: () => true },
        );

      let res = await send(await guestId());
      // The stored guest no longer exists (cleared on the server): every try
      // failed the same way forever. Mint a fresh one and try once more.
      if (res.status === 400 && res.data && res.data.reason === 'invalid_guest') {
        res = await send(await guestId(true));
      }

      if (res.status !== 200) {
        const body = res.data || {};

        /*
         * Two guests, two seats left, both press buy. The server's atomic guard
         * decides it — exactly one wins — and this is what the loser sees.
         *
         * A generic error would be wrong twice over: it does not say what
         * happened, and it leaves the page showing availability that is no
         * longer true, so the next press fails the same way. Reload, re-point at
         * the fresh copy, and say plainly what is left.
         */
        if (body.reason === 'sold_out') {
          const fresh = await loadEvents();
          const updated = (fresh || []).find((e) => String(e._id) === String(selected._id));
          const gone = (body.soldOut || []).map((line) => line.name).filter(Boolean);

          if (updated) {
            setSelected(updated);
            // Drop anything that is no longer buyable rather than silently
            // leaving a count on a type that just sold out.
            setQty((prev) => {
              const next = {};
              for (const type of updated.ticketTypes || []) {
                if (type.available && prev[type._id]) next[type._id] = prev[type._id];
              }
              return next;
            });
          } else {
            setSelected(null);
          }

          setError(
            gone.length
              ? `Nogen kom dig desværre i forkøbet til ${gone.join(' og ')}. Antallet er opdateret.`
              : 'Billetterne blev revet væk imens. Antallet er opdateret.',
          );
          setBusy(false);
          return;
        }

        // Everything else: the server's refusals already carry guest-facing
        // words in the right language — ours would be worse, not better.
        setError(body.message || body.Message || 'Købet kunne ikke gennemføres.');
        setBusy(false);
        return;
      }

      /*
       * A free sign-up never goes to a payment window — it is already confirmed
       * and the tickets are already issued. Confirm it here rather than
       * redirecting: the receipt lives on the heyw.io host, which this app has
       * no URL for, and the guest gets it by SMS and mail anyway.
       */
      if (res.data.zeroValue) {
        // No payment window means no return url, so go to the same confirmation
        // page directly — one place that answers "did it work", not two.
        window.location.href = `${here}/events/${restaurantId}/kvittering?order=${res.data.id}&t=${res.data.token || ''}`;
        return;
      }

      if (res.data.url) {
        window.location.href = res.data.url;
        return;
      }

      setError('Betalingen kunne ikke åbnes. Prøv igen.');
      setBusy(false);
    } catch (err) {
      setError('Der gik noget galt. Prøv igen.');
      setBusy(false);
    }
  };

  if (loading)
    return (
      <div className="ev-page">
        <div className="ev-empty">Henter arrangementer…</div>
      </div>
    );
  if (loadError)
    return (
      <div className="ev-page">
        <div className="ev-empty">{loadError}</div>
      </div>
    );
  if (!enabled)
    return (
      <div className="ev-page">
        <div className="ev-empty">Dette sted sælger ikke billetter.</div>
      </div>
    );
  if (events.length === 0)
    return (
      <div className="ev-page">
        <div className="ev-empty">Ingen kommende arrangementer.</div>
      </div>
    );

  if (selected) {
    const question = selected.extraQuestion;
    return (
      <div className="ev-page">
        <button type="button" className="ev-back" onClick={() => setSelected(null)}>
          ← Tilbage
        </button>

        <div className="ev-card">
          {selected.image ? <img className="ev-image" src={selected.image} alt="" /> : null}
          <div className="ev-body">
            <h1 className="ev-title">{selected.name}</h1>
            <div className="ev-when">{formatWhen(selected)}</div>
            {selected.location ? <div className="ev-where">{selected.location}</div> : null}
            {/* The venue may sell on someone else's behalf; say who holds it. */}
            {selected.organizer ? (
              <div className="ev-where">Arrangør: {selected.organizer.name}</div>
            ) : null}
            {selected.description ? <p className="ev-desc">{selected.description}</p> : null}

            <div className="ev-types">
              {(selected.ticketTypes || []).map((t) => (
                <div className="ev-type" key={t._id}>
                  <div>
                    <div className="ev-type-name">{t.name}</div>
                    {t.description ? <div className="ev-type-desc">{t.description}</div> : null}
                    <div className="ev-type-price">
                      {Number(t.price) > 0 ? money(t.price, currency) : 'Gratis'}
                    </div>
                  </div>
                  {t.available ? (
                    <div className="ev-stepper">
                      <button
                        type="button"
                        aria-label={`Færre: ${t.name}`}
                        onClick={() => setCount(t._id, (qty[t._id] || 0) - 1)}
                      >
                        −
                      </button>
                      <span aria-live="polite">{qty[t._id] || 0}</span>
                      <button
                        type="button"
                        aria-label={`Flere: ${t.name}`}
                        onClick={() => setCount(t._id, (qty[t._id] || 0) + 1)}
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <span className="ev-soldout">Udsolgt</span>
                  )}
                </div>
              ))}
            </div>

            {/* The form appears once there is something to buy. Asking for a
                phone number before a guest has picked a ticket is the wrong way
                round, and it made the page look like a contact form. */}
            <div className="ev-form" hidden={preview.count === 0}>
              <label className="ev-label" htmlFor="ev-name">
                Navn
              </label>
              <input
                id="ev-name"
                className="ev-input"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <label className="ev-label" htmlFor="ev-phone">
                Telefon
              </label>
              <input
                id="ev-phone"
                className="ev-input"
                type="tel"
                autoComplete="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />

              <label className="ev-label" htmlFor="ev-email">
                E-mail
              </label>
              <input
                id="ev-email"
                className="ev-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              {question && question.Label ? (
                <>
                  <label className="ev-label" htmlFor="ev-answer">
                    {question.Label}
                    {question.Required ? '' : ' (valgfrit)'}
                  </label>
                  <input
                    id="ev-answer"
                    className="ev-input"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                  />
                </>
              ) : null}

              {consentTexts ? (
                <div className="ev-consents">
                  <label className="ev-consent" htmlFor="ev-consent-email">
                    <input
                      id="ev-consent-email"
                      type="checkbox"
                      checked={consentEmail}
                      onChange={(e) => setConsentEmail(e.target.checked)}
                    />
                    <span>{consentTexts.email}</span>
                  </label>
                  <label className="ev-consent" htmlFor="ev-consent-sms">
                    <input
                      id="ev-consent-sms"
                      type="checkbox"
                      checked={consentSms}
                      onChange={(e) => setConsentSms(e.target.checked)}
                    />
                    <span>{consentTexts.sms}</span>
                  </label>
                  <div className="ev-fineprint">
                    Læs om dine oplysninger i{' '}
                    <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                      privatlivspolitikken
                    </a>
                    .
                  </div>
                </div>
              ) : null}
            </div>

            {preview.count >= (Number(selected.maxPerOrder) || 10) ? (
              <div className="ev-cap">
                Der kan højst købes {Number(selected.maxPerOrder) || 10} billetter ad gangen.
              </div>
            ) : null}

            {error ? (
              <div className="ev-error" role="alert">
                {error}
              </div>
            ) : null}

            {preview.count > 0 ? (
              <div className="ev-sum">
                <div className="ev-sum-row">
                  <span>
                    {preview.count} {preview.count === 1 ? 'billet' : 'billetter'}
                  </span>
                  <span>{money(preview.tickets, currency)}</span>
                </div>
                {/* The fee is charged on top, so it is named here rather than
                    appearing as a surprise in the payment window. */}
                {preview.fees > 0 ? (
                  <div className="ev-sum-row ev-sum-fee">
                    <span>Billetgebyr</span>
                    <span>{money(preview.fees, currency)}</span>
                  </div>
                ) : null}
                <div className="ev-sum-row ev-sum-total">
                  <span>I alt</span>
                  <span>{money(preview.total, currency)}</span>
                </div>
              </div>
            ) : null}

            <button
              type="button"
              className="ev-buy"
              disabled={busy || preview.count === 0}
              onClick={buy}
            >
              {/* Labelled from the EVENT, not the basket — with nothing chosen
                  yet a paid event was offering to "Tilmeld". */}
              {busy ? 'Et øjeblik…' : preview.anyPaid ? 'Køb billetter' : 'Tilmeld'}
            </button>

            {/* Said BEFORE the purchase, which is what lets the no-withdrawal
                rule for dated events apply. A link, not an extra checkbox. */}
            <div className="ev-fineprint ev-terms-line">
              Ved køb accepterer du{' '}
              <a
                href={termsUrl || `/events/${restaurantId}/${selected._id}/betingelser`}
                target="_blank"
                rel="noopener noreferrer"
              >
                billetbetingelserne
              </a>
              . Billetter til arrangementer på en bestemt dato kan ikke fortrydes.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ev-page">
      {/* A venue that sells only tickets sends the guest straight here from the
          list, so this page is the venue's front door and must say whose. */}
      {venueName ? <div className="ev-venue">{venueName}</div> : null}
      <h1 className="ev-heading">Arrangementer</h1>
      {events.map((event) => (
        <button
          type="button"
          key={event._id}
          className={`ev-card ev-card-button${event.onSale ? '' : ' ev-card-closed'}`}
          onClick={() => event.onSale && open(event)}
          disabled={!event.onSale}
        >
          {event.image ? <img className="ev-image" src={event.image} alt="" /> : null}
          <div className="ev-body">
            <div className="ev-when">{formatWhen(event)}</div>
            <div className="ev-title">{event.name}</div>
            {event.location ? <div className="ev-where">{event.location}</div> : null}
            {!event.onSale ? (
              <div className="ev-closed">{SALE_TEXT[event.saleStatus] || 'Ikke til salg'}</div>
            ) : (event.ticketTypes || []).every((t) => !t.available) ? (
              <div className="ev-closed">Udsolgt</div>
            ) : null}
          </div>
        </button>
      ))}
    </div>
  );
}
